import {SESSION_KEEP_ALIVE_INTERVAL} from '../app.const';
import {fromJson, JsonMappers, toJson} from '../core/data-source/json.mapper';
import {getLanguage} from '../core/lang';
import {HttpService, httpService} from '../core/services/http.service';
import {ChangePasswordRecord} from '../records/change-password.record';
import {ErrorRecord} from '../records/error.record';
import {LoginRecord} from '../records/login.record';
import {MetadataRecord} from '../records/metadata.record';
import {UISettingsRecord} from '../records/ui-settings.record';
import {Role, UserRecord} from '../records/user.record';

export interface AuthResponse {
    showCaptcha?: boolean;
    errors?: ErrorRecord[];
    required2FA?: boolean;
}
const errorMapper = JsonMappers.ArrayOf(() => ErrorRecord).from;
export class AuthService {
    private user: UserRecord;
    public loggedIn: boolean = false;
    private _uiSettings: UISettingsRecord;
    private broadcastChannel: BroadcastChannel;

    constructor() {
        this.broadcastChannel = new BroadcastChannel('auth-service');
        this.broadcastChannel.onmessage = (event) => {
            if (event.data.type === 'logged-out') {
                location.reload();
            } else if (event.data.type === 'switch-role') {
                location.reload();
            } else if (event.data.type === 'switch-language') {
                location.reload();
            }
        };
    }

    keepAlive() {
        const keepAliveInterval = setInterval(() => {
            if (!this.loggedIn) {
                keepAliveInterval && clearInterval(keepAliveInterval);
                document.location.reload();
            }
            this.isLoggedIn();
        }, SESSION_KEEP_ALIVE_INTERVAL);
    }

    async uiSettings(account: string): Promise<UISettingsRecord> {
        if (this._uiSettings) {
            return this._uiSettings;
        }
        return httpService.get('/gt/gt-api/helper/uiSettings?accountName=' + account).then((response: any) => {
            return fromJson(UISettingsRecord, response.data.rows[0]);
        });
    }

    async isLoggedIn(): Promise<boolean> {
        return httpService
            .head('/gt/gt-api/auth/prolongSession', {
                disableErrorHandling: true
            })
            .then((response: any) => {
                this.loggedIn = true;
                return true;
            })
            .catch(() => {
                this.loggedIn = false;
                return false;
            });
    }

    async get2FASecret(): Promise<string> {
        return httpService
            .post('/gt/gt-api/auth/2fa', {
                disableErrorHandling: true
            })
            .then((response: any) => {
                if (response.status == 412) {
                    return null;
                } else {
                    return response.data.secret;
                }
            });
    }

    async activate2FA(code: string): Promise<AuthResponse> {
        return httpService.post('/gt/gt-api/auth/2fa/activate', {code, disableErrorHandling: true}).then(
            (response: any) => {
                return {};
            },
            (response: any) => {
                if (response.status == 201) {
                    return {};
                } else if (response.status == 400) {
                    return {
                        errors: errorMapper([{message: '2FA_INVALID_CODE'}], false)
                    };
                } else if (response.status == 412) {
                    return {
                        errors: errorMapper([{message: '2FA_ALREADY_ACTIVATED'}], false)
                    };
                }
                return {
                    errors: errorMapper([{message: 'UNKNOWN_ERROR'}], false)
                };
            }
        );
    }

    async resetPassword(record: ChangePasswordRecord): Promise<any> {
        return httpService.post('/gt/gt-api/auth/resetPassword', toJson(record)).then((respone: any) => {
            return respone.data;
        });
    }

    async login(loginRecord: LoginRecord): Promise<AuthResponse> {
        return httpService
            .post('/gt/gt-api/auth/login', HttpService.paramsToQuery(toJson(loginRecord)), {
                headers: {'Content-Type': 'application/x-www-form-urlencoded'},
                disableErrorHandling: true
            })
            .then(
                (response: any) => {
                    if (response.data && response.data.success) {
                        this.loggedIn = true;
                        return null;
                    }
                    return {
                        errors: errorMapper(response.data.errors, false),
                        showCaptcha: response.data.rows.length && response.data.rows[0].showCaptcha
                    };
                },
                (response: any) => {
                    if (response.status == 400) {
                        return response.json().then((data: any) => {
                            if (data && data.errors) {
                                if (data.rows) {
                                    const rowItem: any = data.rows[0];
                                    if (rowItem && rowItem.showCaptcha) {
                                        return {
                                            showCaptcha: true,
                                            errors: errorMapper(data.errors, false)
                                        };
                                    }
                                }

                                return {
                                    errors: errorMapper(data.errors, false)
                                };
                            }
                        });
                    } else if (response.status == 412) {
                        return {
                            required2FA: true
                        };
                    } else if (response.status == 404) {
                        return {
                            errors: errorMapper([{message: 'IN_MAINTENANCE'}], false)
                        };
                    } else if (response.status == 400) {
                        return {
                            errors: errorMapper([{message: 'INVALID_LOGIN'}], false)
                        };
                    }

                    return {
                        errors: errorMapper([{message: 'UNKNOWN_ERROR'}], false)
                    };
                }
            );
    }

    async logout(): Promise<void> {
        // sessionStorage.setItem('initialURL', document.location.hash.replace('#', ''));
        return httpService.get('/gt/gt-api/auth/logout').then(() => {
            // this.loggedIn = false;
            // reload page
            this.broadcastChannel.postMessage({type: 'logged-out'});
            document.location.reload();
        });
    }
    async rememberPassword(loginRecord: LoginRecord): Promise<AuthResponse> {
        return httpService
            .get('/gt/gt-api/auth/remember', {
                params: {
                    login: loginRecord.login,
                    account: this.getAccountName()
                },
                disableErrorHandling: true
            })
            .then(
                (response: any) => {
                    if (response.data.success) {
                        return {};
                    } else {
                        return {errors: errorMapper(response.data.errors, false)};
                    }
                },
                (response: any) => {
                    return {
                        errors: errorMapper([{message: response}], false)
                    };
                }
            );
    }

    async getLoginByLink(hash: string): Promise<any> {
        return httpService.post('/gt/gt-api/auth/getLoginByLink', {hash: hash}).then(
            (response: any) => {
                return response.data;
            },
            (response: any) => {
                return response.data;
            }
        );
    }

    async loadUser(): Promise<UserRecord> {
        return httpService.get('/gt/gt-api/users/current').then((response: any) => {
            const user = fromJson(UserRecord, response.data.rows[0]);
            // changeLanguage(this.user.language.code);;
            return user;
        });
    }

    // FIXME move to metadata service
    async loadOrderMetadata(): Promise<MetadataRecord[]> {
        return httpService.get('/gt/gt-api/orders/attributeMetadataMap').then((response: any) => {
            const orderMetadata: MetadataRecord[] = [];
            response.data.rows[0].ORDER.filter((meta: any) => meta.group !== 'return_packages').map((data: any) => {
                let metadata: MetadataRecord = fromJson(MetadataRecord, data);
                orderMetadata.push(metadata);
            });
            return orderMetadata;
        });
    }

    // FIXME move to metadata service
    traslateMetadata(metadata: MetadataRecord[]): MetadataRecord[] {
        const languageCode = getLanguage();
        return metadata.map((data: MetadataRecord) => {
            if (data.title) {
                data.title = data.title && data.title.indexOf('{') != -1 ? JSON.parse(data.title) : {en: data.title};
                data.displayName = languageCode in data.title ? data.title[languageCode] : data.title.en || data.name;
            } else {
                data.displayName = data.name;
            }
            return data;
        });
    }

    async changeRole(role: Role): Promise<any> {
        return httpService
            .post(
                '/gt/gt-api/users/change_role',
                HttpService.paramsToQuery({
                    newRole: role
                }),
                {headers: {'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'}}
            )
            .then((response) => {
                this.broadcastChannel.postMessage({type: 'switch-role', role: role});
                if (response.data.success) location.reload();
            });
    }

    async changeLanguage(lang: string): Promise<any> {
        return httpService
            .post(
                '/gt/gt-api/users/change_language',
                HttpService.paramsToQuery({
                    lang: lang
                }),
                {headers: {'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'}}
            )
            .then((response) => {
                this.broadcastChannel.postMessage({type: 'switch-language', lang: lang});
                if (response.data.success) location.reload();
            });
    }

    getAccountName(): string {
        if (!document.location) return '';
        if (document.location.pathname === '/') {
            // is domain scheme
            return document.location.host.split('.')[0];
        } else {
            return document.location.pathname.replace('/', '');
        }
    }
}
